import { Button, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

type DepartmentItem = { id: string; name: string };
type PositionItem = { id: string; title: string };
type SOPItem = { id: string; name: string };
type FlowItem = { id: string; name: string };

const ChecklistMappingFormSchema = z.object({
  sopId: z.string().nullable().optional(),
  flowId: z.string().nullable().optional(),
  positionId: z.string().nullable().optional(),
  departmentId: z.string().nullable().optional(),
});
export type ChecklistMappingFormValues = z.infer<
  typeof ChecklistMappingFormSchema
>;

export function ChecklistMappingForm({
  initialValues,
  onSubmit,
  submitting,
  checklistDepartmentId,
}: {
  initialValues?: Partial<ChecklistMappingFormValues>;
  onSubmit: (values: ChecklistMappingFormValues) => void;
  submitting?: boolean;
  checklistDepartmentId?: string | null; // Filter lookups by checklist's department
}) {
  const methods = RHF.useForm<ChecklistMappingFormValues>({
    resolver: zodResolver(ChecklistMappingFormSchema),
    defaultValues: {
      sopId: null,
      flowId: null,
      positionId: null,
      departmentId: null,
      // Auto-select checklist's department if not provided in initialValues
      ...(initialValues?.departmentId === undefined && checklistDepartmentId
        ? { departmentId: checklistDepartmentId }
        : {}),
      ...initialValues,
    },
  });

  // Watch form values for preview
  const sopId = RHF.useWatch({ control: methods.control, name: "sopId" });
  const flowId = RHF.useWatch({ control: methods.control, name: "flowId" });
  const positionId = RHF.useWatch({
    control: methods.control,
    name: "positionId",
  });
  const departmentId = RHF.useWatch({
    control: methods.control,
    name: "departmentId",
  });

  // Fetch names for selected items
  const { data: sopItem } = useQuery({
    queryKey: ["checklists-sops", "preload", sopId],
    queryFn: () =>
      hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/checklists/sops/lookup`,
        sopId ?? "",
      ),
    enabled: !!sopId,
  });

  const { data: flowItem } = useQuery({
    queryKey: ["checklists-flows", "preload", flowId],
    queryFn: () =>
      hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/checklists/flows/lookup`,
        flowId ?? "",
      ),
    enabled: !!flowId,
  });

  const { data: positionItem } = useQuery({
    queryKey: ["checklists-positions", "preload", positionId],
    queryFn: async () => {
      const item = await hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/checklists/positions/lookup`,
        positionId ?? "",
      );
      if (!item) return null;
      return { id: item.id, title: item.name };
    },
    enabled: !!positionId,
  });

  const { data: departmentItem } = useQuery({
    queryKey: ["checklists-departments", "preload", departmentId],
    queryFn: () =>
      hydrateLookupItem<{ id: string; name: string }>(
        `${config.apiUrl}/checklists/departments/lookup`,
        departmentId ?? "",
      ),
    enabled: !!departmentId,
  });

  // Fetch position for selected SOP
  const { data: sopPosition } = useQuery({
    queryKey: ["checklists-sops-position", sopId],
    queryFn: async () => {
      if (!sopId) return null;
      const res = await fetch(
        `${config.apiUrl}/checklists/sops/${sopId}/position`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data.positionId as string | null;
    },
    enabled: !!sopId,
  });

  // Fetch position for selected Flow
  const { data: flowPosition } = useQuery({
    queryKey: ["checklists-flows-position", flowId],
    queryFn: async () => {
      if (!flowId) return null;
      const res = await fetch(
        `${config.apiUrl}/checklists/flows/${flowId}/position`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data.positionId as string | null;
    },
    enabled: !!flowId,
  });

  // Auto-select position when SOP or Flow has a position mapping
  useEffect(() => {
    // Only auto-select if position is not already set
    if (positionId) return;

    // Priority: SOP position > Flow position
    const newPositionId = sopPosition ?? flowPosition ?? null;
    if (newPositionId) {
      methods.setValue("positionId", newPositionId);
    }
  }, [sopPosition, flowPosition, positionId, methods]);

  // Clear SOP and Flow when position is cleared
  useEffect(() => {
    if (!positionId) {
      if (sopId) methods.setValue("sopId", null);
      if (flowId) methods.setValue("flowId", null);
    }
  }, [positionId, sopId, flowId, methods]);

  // Determine if preview should be shown
  const hasMapping = !!(sopId || flowId || positionId || departmentId);

  return (
    <FormRoot<ChecklistMappingFormValues>
      methods={methods}
      onSubmit={onSubmit}
      className="space-y-4"
    >
      <FormInfiniteCombobox<DepartmentItem>
        name="departmentId"
        label="ພະແນກ"
        queryKey={["checklists-departments"]}
        queryFn={(args) =>
          fetchLookupForInfinite(
            `${config.apiUrl}/checklists/departments/lookup`,
            args,
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(
            `${config.apiUrl}/checklists/departments/lookup`,
            id,
          )
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ພະແນກ"
        clearable={!checklistDepartmentId}
        disabled={!!checklistDepartmentId}
      />

      <FormInfiniteCombobox<PositionItem>
        name="positionId"
        label="ຕໍາແໜ່ງ"
        queryKey={[
          "checklists-positions",
          ...(checklistDepartmentId ? [checklistDepartmentId] : []),
        ]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/checklists/positions/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (checklistDepartmentId) {
            url.searchParams.set("departmentId", checklistDepartmentId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return {
            items: items.map((item: { id: string; name: string }) => ({
              id: item.id,
              title: item.name,
            })),
            nextPage,
          };
        }}
        preloadQueryFn={async (id) => {
          const item = await hydrateLookupItem<{ id: string; name: string }>(
            `${config.apiUrl}/checklists/positions/lookup`,
            id,
          );
          if (!item) return null;
          return { id: item.id, title: item.name };
        }}
        getLabel={(item) => item.title}
        getValue={(item) => item.id}
        placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ)"
        clearable
      />

      <FormInfiniteCombobox<SOPItem>
        name="sopId"
        label="SOP"
        queryKey={[
          "checklists-sops",
          ...(checklistDepartmentId ? [checklistDepartmentId] : []),
          ...(positionId ? [positionId] : []),
        ]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/checklists/sops/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (checklistDepartmentId) {
            url.searchParams.set("departmentId", checklistDepartmentId);
          }
          if (positionId) {
            url.searchParams.set("positionId", positionId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/checklists/sops/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ SOP (ບໍ່ບັງຄັບ)"
        clearable
        disabled={!positionId}
      />

      <FormInfiniteCombobox<FlowItem>
        name="flowId"
        label="Flow"
        queryKey={[
          "checklists-flows",
          ...(checklistDepartmentId ? [checklistDepartmentId] : []),
          ...(positionId ? [positionId] : []),
        ]}
        queryFn={async (args) => {
          const url = new URL(`${config.apiUrl}/checklists/flows/lookup`);
          if (args.search && args.search.trim().length > 0) {
            url.searchParams.set("q", args.search);
          }
          if (checklistDepartmentId) {
            url.searchParams.set("departmentId", checklistDepartmentId);
          }
          if (positionId) {
            url.searchParams.set("positionId", positionId);
          }
          const limit = 20;
          const page =
            typeof args.pageParam === "number" && args.pageParam > 0
              ? args.pageParam
              : 1;
          const skip = (page - 1) * limit;
          url.searchParams.set("limit", String(limit));
          url.searchParams.set("skip", String(skip));

          const data = await fetch(url.toString(), {
            credentials: "include",
          }).then((r) => r.json());
          const items = Array.isArray(data.items) ? data.items : [];
          const total =
            typeof data.total === "number" ? data.total : items.length;
          const nextPage = skip + limit < total ? page + 1 : null;

          return { items, nextPage };
        }}
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/checklists/flows/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກ Flow (ບໍ່ບັງຄັບ)"
        clearable
        disabled={!positionId}
      />

      {/* Real-time Preview Section */}
      {hasMapping && (
        <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
          <h4 className="font-semibold text-sm">ຕົວຢ່າງ: Checklist ນີ້ຈະປາກົດທີ່:</h4>
          <div className="space-y-1 text-sm">
            {(sopItem || flowItem) && (
              <div>
                <span className="font-medium text-muted-foreground">
                  ການຜູກກັບກັບວຽກ:
                </span>
                <ul className="mt-1 ml-2 list-inside list-disc">
                  {sopItem && <li>SOP: {sopItem.name}</li>}
                  {flowItem && <li>Flow: {flowItem.name}</li>}
                </ul>
              </div>
            )}
            {(positionItem || departmentItem) && (
              <div>
                <span className="font-medium text-muted-foreground">
                  ພະແນກ/ຕໍາແໜ່ງ:
                </span>
                <ul className="mt-1 ml-2 list-inside list-disc">
                  {departmentItem && <li>ພະແນກ: {departmentItem.name}</li>}
                  {positionItem && <li>ຕໍາແໜ່ງ: {positionItem.title}</li>}
                </ul>
              </div>
            )}
            {(positionItem || departmentItem) && (
              <div className="border-t pt-1">
                <span className="font-medium text-muted-foreground">
                  ພະນັກງານທີ່ຕ້ອງເຮັດ:
                </span>
                <p className="mt-1 ml-2 text-muted-foreground">
                  {positionItem
                    ? `ພະນັກງານໃນຕໍາແໜ່ງ "${positionItem.title}"`
                    : departmentItem
                      ? `ພະນັກງານໃນພະແນກ "${departmentItem.name}"`
                      : "-"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
